// Esquema SQLite consolidado v1 de offline-v2 (STOR-03).
//
// Todo el esquema (Tier A captura + Infra de sync + Tier B catálogos) vive en UN
// solo `statements[]` bajo un único `toVersion: 1` (ver migrations.js) para esquivar
// el bug empty-DB #320 del plugin: con múltiples upgrades, una DB vacía puede ejecutar
// SOLO el último upgrade → esquema incompleto en instalaciones nuevas (req STOR-04).
//
// REGLAS (no romper):
//   - Cada statement es idempotente: `CREATE TABLE IF NOT EXISTS` / `CREATE INDEX IF NOT EXISTS`.
//   - Un statement por elemento del array (cada CREATE TABLE y cada CREATE INDEX por separado);
//     el plugin los corre en orden dentro del mismo toVersion.
//   - Cambios futuros de esquema = una NUEVA entrada `{ toVersion: 2, statements: [...] }`
//     agregada en migrations.js, NUNCA una edición a este array.
//   - Módulo PURO: sin imports de @capacitor* ni de ningún plugin. Lo consumen tanto el
//     StorageAdapter como el gate de test (empty-db.spec.js).
//   - Binarios NUNCA son columnas: solo `image_ref` / `local_path` (binario fuera de la BD).
//   - `deleted_at` NO existe en el cliente: es columna server-side; el cliente consume
//     tombstones vía el array `deleted[]` del pull (ver 02-sync-contract.md §3).
//
// DDL transcrito EXACTO de tasks/offline-v2/01-modelo-datos.md §3/§4/§5.

export const V1_STATEMENTS = [
  // ── Tier A — captura (§3) ─────────────────────────────────────────────────
  `CREATE TABLE IF NOT EXISTS occurrences (
    client_uuid TEXT PRIMARY KEY,
    id INTEGER,
    form_id INTEGER, form_assignment_id INTEGER, form_assignment_role_id INTEGER,
    location_id INTEGER, role_id INTEGER, end_date TEXT, score REAL,
    status TEXT,
    server_version INTEGER,
    updated_at TEXT, created_at TEXT,
    comments TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS idx_occ_status   ON occurrences(status)`,
  `CREATE INDEX IF NOT EXISTS idx_occ_location ON occurrences(location_id)`,
  `CREATE INDEX IF NOT EXISTS idx_occ_form     ON occurrences(form_id)`,

  `CREATE TABLE IF NOT EXISTS answers (
    client_uuid TEXT PRIMARY KEY,
    occurrence_id TEXT,
    item_id INTEGER,
    value TEXT,
    current_points REAL, total_points REAL,
    extra_properties TEXT,
    image_ref TEXT, local_path TEXT,
    created_at TEXT, updated_at TEXT,
    UNIQUE(occurrence_id, item_id)
  )`,
  `CREATE INDEX IF NOT EXISTS idx_ans_occ ON answers(occurrence_id)`,

  `CREATE TABLE IF NOT EXISTS addons (
    client_uuid TEXT PRIMARY KEY,
    occurrence_id TEXT, item_id INTEGER,
    type TEXT,
    answer TEXT, answer_ref TEXT, image_ref TEXT, local_path TEXT,
    created_at TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS idx_add_occ ON addons(occurrence_id)`,

  `CREATE TABLE IF NOT EXISTS signatures (
    client_uuid TEXT PRIMARY KEY,
    occurrence_id TEXT, form_assignment_id INTEGER,
    role TEXT, image_ref TEXT, local_path TEXT, signature_kind TEXT, created_at TEXT,
    signed_by INTEGER, signed_name TEXT, is_auditor INTEGER, is_audited INTEGER,
    audited_rol_name TEXT, audited_name TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS idx_sig_occ ON signatures(occurrence_id)`,

  `CREATE TABLE IF NOT EXISTS tasks (
    client_uuid TEXT PRIMARY KEY,
    server_id INTEGER,
    occurrence_id TEXT,
    title TEXT, priority INTEGER, role_id INTEGER, assigned_to_id INTEGER,
    location_id INTEGER, item_id INTEGER, category TEXT, category2 TEXT,
    area_id INTEGER, hierarchy_site_id INTEGER, hierarchy_subcategory_id INTEGER,
    end_date TEXT, state TEXT, enforce_evidence INTEGER, short_name TEXT,
    origin TEXT, server_version INTEGER, updated_at TEXT, created_at TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS idx_task_occ    ON tasks(occurrence_id)`,
  `CREATE INDEX IF NOT EXISTS idx_task_server ON tasks(server_id)`,
  `CREATE INDEX IF NOT EXISTS idx_task_state  ON tasks(state)`,

  `CREATE TABLE IF NOT EXISTS task_evidence (
    uuid TEXT PRIMARY KEY,
    task_client_uuid TEXT, task_server_id INTEGER,
    kind TEXT,
    image_ref TEXT, local_path TEXT, comment TEXT, filename TEXT, created_at TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS idx_ev_task ON task_evidence(task_client_uuid)`,

  `CREATE TABLE IF NOT EXISTS photo_meta (
    image_ref TEXT PRIMARY KEY,
    parent_uuid TEXT, parent_kind TEXT, item_id INTEGER, local_path TEXT,
    byte_length INTEGER,
    upload_status TEXT,
    created_at TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS idx_photo_status ON photo_meta(upload_status)`,

  // ── Infra de sync (§4) ────────────────────────────────────────────────────
  `CREATE TABLE IF NOT EXISTS outbox (
    seq INTEGER PRIMARY KEY AUTOINCREMENT,
    mutation_uuid TEXT,
    client_uuid TEXT, entity TEXT, op TEXT,
    body TEXT,
    status TEXT,
    retries INTEGER, next_attempt_at INTEGER, last_error TEXT, created_at INTEGER,
    user_id INTEGER,
    done_at INTEGER
  )`,
  `CREATE INDEX IF NOT EXISTS idx_outbox_status ON outbox(status, next_attempt_at)`,

  `CREATE TABLE IF NOT EXISTS id_mapping (
    entity TEXT, client_uuid TEXT, server_id INTEGER,
    PRIMARY KEY(entity, client_uuid)
  )`,
  `CREATE INDEX IF NOT EXISTS idx_idmap_server ON id_mapping(entity, server_id)`,

  `CREATE TABLE IF NOT EXISTS meta (key TEXT PRIMARY KEY, value TEXT)`,

  `CREATE TABLE IF NOT EXISTS sync_notifications (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    entity TEXT, client_uuid TEXT, kind TEXT, dismissed INTEGER, created_at INTEGER, user_id INTEGER
  )`,

  // ── Tier B — catálogos read-only (§5) ─────────────────────────────────────
  `CREATE TABLE IF NOT EXISTS c_forms       (id INTEGER PRIMARY KEY, organization_id INTEGER, data TEXT, updated_at TEXT)`,
  `CREATE TABLE IF NOT EXISTS c_form_groups (id INTEGER PRIMARY KEY, form_id INTEGER, data TEXT)`,
  `CREATE INDEX IF NOT EXISTS idx_fg_form ON c_form_groups(form_id)`,
  `CREATE TABLE IF NOT EXISTS c_form_items  (id INTEGER PRIMARY KEY, group_id INTEGER, data TEXT)`,
  `CREATE INDEX IF NOT EXISTS idx_fi_group ON c_form_items(group_id)`,
  `CREATE TABLE IF NOT EXISTS c_locations   (id INTEGER PRIMARY KEY, org_id INTEGER, name TEXT, data TEXT)`,
  `CREATE INDEX IF NOT EXISTS idx_loc_org ON c_locations(org_id)`,
  `CREATE TABLE IF NOT EXISTS c_users       (id INTEGER PRIMARY KEY, org_id INTEGER, full_name TEXT, data TEXT)`,
  `CREATE INDEX IF NOT EXISTS idx_usr_org ON c_users(org_id)`,
  `CREATE TABLE IF NOT EXISTS c_roles       (id INTEGER PRIMARY KEY, name TEXT, data TEXT)`,

  // GAPS (no se cachean hoy; necesarios para crear tasks offline — req PULL-03)
  `CREATE TABLE IF NOT EXISTS c_tags        (id INTEGER PRIMARY KEY, name TEXT, data TEXT)`,
  `CREATE TABLE IF NOT EXISTS c_areas       (id INTEGER PRIMARY KEY, category TEXT, name TEXT, data TEXT)`,
  `CREATE TABLE IF NOT EXISTS c_hierarchies (id INTEGER PRIMARY KEY, kind TEXT, parent_id INTEGER, name TEXT, data TEXT)`,
  `CREATE INDEX IF NOT EXISTS idx_hier_parent ON c_hierarchies(parent_id)`,

  `CREATE TABLE IF NOT EXISTS c_catalog_elements (id INTEGER PRIMARY KEY, catalog_id INTEGER, data TEXT)`
]
